import { ArrowLeftRight, Sparkles } from "lucide-react";
import type { Account, Trip } from "../../types";
import { money } from "../../lib/finance";
import Avatar from "../../components/Avatar";
import { Private } from "../../components/Privacy";
import { inferCurrencyForDestination } from "../../lib/countries";
import { cleanTripAccountName } from "../../lib/trips";
import type { WalletStat } from "../../hooks/use-trip-stats";

export interface TripWalletsSectionProps {
  trip: Trip;
  walletBalances: WalletStat[];
  pastTripsWithLeftover: {
    fromTrip: Trip;
    account: Account;
    currency: string;
    balance: number;
  }[];
  busy: boolean;
  onRollover: (item: {
    fromTrip: Trip;
    account: Account;
    currency: string;
    balance: number;
  }) => Promise<void> | void;
  onAddTransaction: (prefill: {
    tripId?: string;
    tripDestination?: string;
    type?: "Expense" | "Income" | "Transfer";
    accountId?: string;
  }) => void;
}

export default function TripWalletsSection({
  trip,
  walletBalances,
  pastTripsWithLeftover,
  busy,
  onRollover,
  onAddTransaction,
}: TripWalletsSectionProps) {
  if (walletBalances.length === 0 && pastTripsWithLeftover.length === 0) {
    return null;
  }

  return (
    <div className="trip-section">
      <div className="section-heading">
        <h3>Cash wallets</h3>
        <span className="subtle-tag">{walletBalances.length}</span>
      </div>

      {pastTripsWithLeftover.length > 0 && (
        <div className="trip-rollover-banner">
          <div className="trip-rollover-banner-head">
            <Sparkles size={16} />
            <strong>Leftover cash from past trips available:</strong>
          </div>
          <div className="trip-rollover-banner-list">
            {pastTripsWithLeftover.map((item) => (
              <div key={item.account.id} className="trip-rollover-banner-row">
                <div className="trip-rollover-banner-info">
                  <span className="trip-rollover-banner-source">
                    {item.fromTrip.name} · {cleanTripAccountName(item.account.name, item.fromTrip.name)}
                  </span>
                  <strong className="trip-rollover-banner-amount">
                    <Private>{money(item.balance, item.currency)}</Private>
                  </strong>
                </div>
                <button
                  type="button"
                  className="primary small trip-rollover-btn"
                  disabled={busy}
                  onClick={() => void onRollover(item)}
                >
                  <ArrowLeftRight size={13} /> Roll over {item.currency}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {walletBalances.length > 0 && (
        <div className="trip-wallets-list">
          {walletBalances.map((w) => (
            <div key={w.account.id} className="trip-wallet-card">
              <div className="trip-wallet-head">
                <Avatar owner={w.account.owner} size={30} />
                <div className="trip-wallet-info">
                  <strong>{cleanTripAccountName(w.account.name, trip.name)}</strong>
                  <span>{w.account.owner} · {w.currency}</span>
                </div>
              </div>
              <div className="trip-wallet-right">
                <div className="trip-wallet-balance">
                  <Private>{money(w.balance, w.currency)}</Private>
                </div>
                {w.spent > 0 && (
                  <div className="trip-wallet-spent-sub">
                    <Private>{money(w.spent, w.currency)}</Private> spent
                  </div>
                )}
                <button
                  type="button"
                  className="text-button small subtle trip-wallet-transfer-btn"
                  onClick={() =>
                    onAddTransaction({
                      tripId: trip.id,
                      tripDestination:
                        trip.destinations.find(
                          (d) => inferCurrencyForDestination(d) === w.currency,
                        ) || trip.destinations[0],
                      type: "Transfer",
                      accountId: w.account.id,
                    })
                  }
                  title={`Transfer into or from ${w.currency} wallet`}
                >
                  ⇄ Transfer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
